import { assertEvent } from 'xstate';

import { type Guard, type Modification } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines if adding a modification exceeds the maximum allowed multiple
 * quantities for a modification group.
 */
export const checkIfExceedsAllowedMultipleQuantities: Guard = function (args): boolean {
  const { context, event } = args;

  assertEvent(event, [
    'customization.add-modification',
    'customization.dressing.increase-portions',
  ]);

  const { ingredientId } = event;
  const { allModificationsMap, modifications, modificationLimits } = context;

  // ─── Missing Data ────────────────────────────────────────────────────

  const hasMissingData = !modifications || !allModificationsMap || !modificationLimits;

  if (hasMissingData) {
    return false;
  }

  // ─────────────────────────────────────────────────────────────────────

  const modification = allModificationsMap.get(ingredientId);

  if (!modification) {
    return false;
  }

  // ─── Multiple Quantity Allowed ───────────────────────────────────────

  const modificationGroupLimits = modificationLimits[modification.modificationGroupName]; // prettier-ignore
  const allowsMultipleQuantity = modificationGroupLimits?.allowMultipleQuantity ?? true; // prettier-ignore

  if (allowsMultipleQuantity) {
    return false;
  }

  // ─────────────────────────────────────────────────────────────────────

  const { active } = modifications;

  const findMatchingModification = findMatchingModificationFactory(ingredientId); // prettier-ignore
  const matchingModification = active.find(findMatchingModification);
  const alreadyHasModification = Boolean(matchingModification);

  return alreadyHasModification;
};

// ─────────────────────────────────────────────────────────────────────────────

function findMatchingModificationFactory(ingredientId: string) {
  return function (modification: Modification) {
    return modification.ingredient?.id === ingredientId;
  };
}
